import { useTranslation } from 'react-i18next';
import { OrganizationTotals, Skeleton, withStyles } from 'vopay';
import { BodyText, Card, UtilButton } from 'UI';
import formatOrganizationBalance from './formatOrganizationBalance.helper';
import styles from './OrganizationSummaryCards.module.scss';

const cn = withStyles(styles);

type OrganizationSummaryCardsProps = {
  fetching: boolean;
  /** Undefined until the organization has loaded. */
  totals: OrganizationTotals | undefined;
  /** Opens the partner list in chart view; left out when the partner may not open that page. */
  onViewPartners?: () => void;
  /** Opens the account list in chart view; left out when the partner may not open that page. */
  onViewAccounts?: () => void;
};

type SummaryCardProps = {
  fetching: boolean;
  /** One figure, or one per currency for the balance. */
  figures: string[];
  label: string;
  onView?: () => void;
  viewLabel?: string;
  className?: string;
  'data-testid': string;
};

function SummaryCard({
  fetching,
  figures,
  label,
  onView,
  viewLabel,
  className,
  'data-testid': testId,
}: SummaryCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Entities.OrganizationSummaryCards' });

  return (
    <Card variant="section" className={cn('card', className)} data-testid={testId}>
      <div className={cn('figures')}>
        {fetching ? (
          <Skeleton width={40} height={32} />
        ) : (
          // Figma `H3/Large Title 24/Emphasized` on a figure, not a heading, so the style lives in the
          // module rather than in a <Title> (the same call the Accounts overview's totals make).
          figures.map((figure) => (
            <span key={figure} className={cn('figure')} data-testid="organizationSummaryFigure">
              {figure}
            </span>
          ))
        )}

        {/* Body/16/Regular, Text/Secondary75 */}
        <BodyText className={cn('label')} data-testid="organizationSummaryLabel">
          {label}
        </BodyText>
      </div>

      {onView && (
        // Figma `Utility Button`: 13px medium, 22px tall, with `arrow_forward`.
        <UtilButton className={cn('viewLink')} iconRight="arrow_forward" aria-label={viewLabel} onClick={onView}>
          {t('viewLink', { defaultValue: 'View' })}
        </UtilButton>
      )}
    </Card>
  );
}

/**
 * The four cards above the tree (spec 6.2): partners, accounts and sub-accounts across the whole
 * organization — not just what is expanded — and the total balance, one line per currency when the
 * book holds more than one.
 */
export default function OrganizationSummaryCards({
  fetching,
  totals,
  onViewPartners,
  onViewAccounts,
}: OrganizationSummaryCardsProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Entities.OrganizationSummaryCards' });

  const numPartners = totals?.NumPartners ?? 0;
  const numAccounts = totals?.NumAccounts ?? 0;
  const numSubAccounts = totals?.NumSubAccounts ?? 0;

  const balances = Object.entries(totals?.Balances ?? {});
  const [singleCurrency] = balances.length === 1 ? balances[0] : [null];

  const balanceFigures =
    balances.length === 0
      ? [formatOrganizationBalance('0.00', null)]
      : balances.map(([currency, amount]) =>
          formatOrganizationBalance(amount, currency, { withCode: !singleCurrency }),
        );

  return (
    <div className={cn('cards')} data-testid="organizationSummaryCards">
      <SummaryCard
        fetching={fetching}
        figures={[String(numPartners)]}
        label={t('partners', { count: numPartners, defaultValue: 'Partners' })}
        onView={onViewPartners}
        viewLabel={t('viewPartnersLabel', { defaultValue: 'View partners in chart view' })}
        data-testid="organizationSummaryCard-partners"
      />

      <SummaryCard
        fetching={fetching}
        figures={[String(numAccounts)]}
        label={t('accounts', { count: numAccounts, defaultValue: 'Accounts' })}
        onView={onViewAccounts}
        viewLabel={t('viewAccountsLabel', { defaultValue: 'View accounts in chart view' })}
        data-testid="organizationSummaryCard-accounts"
      />

      <SummaryCard
        fetching={fetching}
        figures={[String(numSubAccounts)]}
        label={t('subAccounts', { count: numSubAccounts, defaultValue: 'Sub-accounts' })}
        data-testid="organizationSummaryCard-subAccounts"
      />

      <SummaryCard
        fetching={fetching}
        figures={balanceFigures}
        label={
          singleCurrency
            ? t('totalBalanceInCurrency', { currency: singleCurrency, defaultValue: 'Total balance ({{currency}})' })
            : t('totalBalance', { defaultValue: 'Total balance' })
        }
        className={cn('balanceCard')}
        data-testid="organizationSummaryCard-balance"
      />
    </div>
  );
}
